<template>
  <v-card class="border physical-activity-summary">
    <v-container>
      <v-row>
        <v-col cols="6" style="border-right: 1px solid #ccc">
          <v-row class="ma-2">
            <v-col cols="12">
              <h3>{{ $ucfirst($t("pa.dailyActivityGoalSteps")) }}</h3>
              <apexchart :options="chartOptions" :series="chartSeries" />
            </v-col>
          </v-row>
          <v-row class="ma-2">
            <v-col cols="12">
              <h3>{{ $ucfirst($t("pa.stepPlan")) }}</h3>
              <v-row>
                <v-col cols="2">
                  <target-sport-no-sport-icon type="no sport" />
                </v-col>
                <v-col>
                  {{ stepsNoSport }}
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="2">
                  <target-sport-no-sport-icon type="sport" />
                </v-col>
                <v-col>
                  {{ $ucfirst($t("pa.ifYouReportSports")) }}
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="6">
          <v-row class="ma-2">
            <v-col cols="12">
              <h3>
                {{ $ucfirst($t("pa.weeklySportsGoal")) }} ({{ $t("pa.kcal") }})
              </h3>
              <v-row class="mx-1 my-3">
                <v-col>
                  <v-row>
                    <h4>{{ $ucfirst($t("pa.sportsGoal")) }}</h4>
                  </v-row>
                  <v-row>
                    <v-col cols="12">
                      <v-row class="mb-2" style="align-items: center">
                        <span
                          class="mr-2 border"
                          v-if="
                            minIntensityLabel &&
                            minIntensityLabel === 'expertTool.intensity.L.long'
                          "
                        >
                          <smart-icon
                            icon="mdi-emoticon-neutral-outline"
                            class="pa-3"
                          />
                        </span>
                        <span
                          class="mr-2 border"
                          v-if="
                            minIntensityLabel &&
                            minIntensityLabel === 'expertTool.intensity.M.long'
                          "
                        >
                          <smart-icon
                            icon="mdi-emoticon-happy-outline"
                            class="pa-3"
                          />
                        </span>
                        <span
                          class="mr-2 border"
                          v-if="
                            minIntensityLabel &&
                            minIntensityLabel === 'expertTool.intensity.H.long'
                          "
                        >
                          <smart-icon icon="mdi-emoticon-outline" class="pa-3" />
                        </span>
                        <span v-if="minIntensityLabel">
                          {{ $t(`${minIntensityLabel}`) }} {{ $t("expertTool.columnNames.intensity") }}
                        </span>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row class="mb-2" style="align-items: center">
                        <span class="mr-2 border">
                          <smart-icon icon="mdi-calendar-outline" class="pa-3" />
                        </span>
                        <span> {{ frequencyLabel }} {{ $t("sessions") }} </span>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row class="mb-2" style="align-items: center">
                        <span class="mr-2 border">
                          <smart-icon icon="mdi-timer-outline" class="pa-3" />
                        </span>
                        <span>
                          {{ sessionDurationLabel }} {{ $t("minutes") }}
                        </span>
                      </v-row>
                    </v-col>
                    <v-col cols="12">
                      <v-row class="mb-2" style="align-items: center">
                        <span class="mr-2 border">
                          <smart-icon icon="mdi-dumbbell" class="pa-3" />
                        </span>
                        {{ strengthTraining }} {{ $t("sessions") }}
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row class="ma-2">
            <v-col cols="12">
              <h3>{{ $t("pa.exercisePlan") }}</h3>
              <v-row
                style="
                  border-left: 1px solid #ccc;
                  border-bottom: 1px solid #ccc;
                  min-height: 150px;
                "
              >
                <v-col
                  v-for="activityDay in activityDays"
                  v-bind:key="activityDay.id"
                  style="
                    border-top: 1px solid #ccc;
                    border-right: 1px solid #ccc;
                  "
                  class="
                    v-patient-overview-pa-goal-setting-weekly-sports-goal__activity-days__day
                  "
                >
                  <v-row
                    style="text-align: center; border-bottom: 1px solid #ccc"
                  >
                    <v-col cols="12" style="">
                      {{ activityDay.date | dayName }}
                    </v-col>
                  </v-row>
                  <v-row
                    v-for="activity in activityDay.activities"
                    v-bind:key="activity.id"
                  >
                    <v-col>
                      <div style="text-align: center">
                        <activity-icon
                          :activityType="activity.patientActivityType.key"
                        />
                      </div>
                      <div style="text-align: center">
                        {{ activity.duration }} min
                      </div>
                      <div style="text-align: center">
                        {{ activity.patientActivityType.activityType.name }}
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
              <v-row v-if="strengthTraining">
                <v-col cols="3"> {{ $t("pa.strengthTraining") }}: </v-col>
                <v-col v-for="i in strengthTraining" v-bind:key="i" cols="1">
                  <smart-icon icon="mdi-dumbbell" class="mr-2" />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
  </v-card>
</template>

<script>
import moment from "moment";
import gql from "graphql-tag";
import TargetSportNoSportIcon from "../../../components/Parts/TargetSportNoSportIcon.vue";
import SmartIcon from "../../../components/Parts/SmartIcon.vue";
import ActivityIcon from "../../../components/Parts/ActivityIcon.vue";
import { mapGetters } from "vuex";

/**
 * Component PatientOverviewPASummary.vue
 *
 * @module PatientOverviewPASummary
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [deprecated] app/src/APP_DASHBOARD/templates/views/Patient/PA/PatientOverviewPASummary.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "PatientOverviewPAGoalSummary",
  components: {
    TargetSportNoSportIcon,
    SmartIcon,
    ActivityIcon,
  },
  filters: {
    dayName: function (date) {
      return date ? moment(date).format("dddd") : ""; // TODO: translate
    },
  },
  computed: {
    ...mapGetters({
      patientId: "patientSummary/id",
    }),
  },
  data: function () {
    return {
      stepsNoSport: null,
      minSessions: null,
      maxSessions: null,
      minSessionDuration: null,
      maxSessionDuration: null,
      feelScale: null,
      strengthTraining: null,
      sessionDurationLabel: null,
      frequencyLabel: null,
      minIntensityLabel: null,
      programDurationLabel: null,
      activityDays: [],
      chartSeries: [],
      chartOptions: {
        chart: {
          // height: 450,
          toolbar: {
            show: false,
          },
          zoom: {
            enabled: false,
          },
        },
        colors: [this.$color.coro["info-500"]],
        legend: {
          show: false,
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          curve: "stepline",
          lineCap: "butt",
        },
        grid: {
          row: {
            colors: ["#f3f3f3", "transparent"], // takes an array which will be repeated on columns
            opacity: 0.5,
          },
        },
        yaxis: {
          title: {
            text: "Steps",
            style: {
              fontSize: '0.75rem'
            },
          },
          // forceNiceScale: true,
          min: 0,
          max: 8500,
          labels: {
            style: {
              fontSize: '0.75rem'
            }
          }
          // tickAmount: 5,
        },
        xaxis: {
          title: {
            text: this.$ucfirst(this.$t("alerts.time")),
            style: {
              fontSize: '0.75rem'
            },
          },
          categories: [],
          labels: {
            style: {
              fontSize: '0.75rem'
            },
          }
        },
        tooltip: {
          style: {
            fontSize: '0.85rem'
          },
        },
        noData: {
          text: this.$t("noData"),
          align: "center",
          verticalAlign: "middle",
          offsetX: 0,
          offsetY: -32,
          style: {
            // color: this.$color.coro["primary-500"],
            fontSize: "22px",
            fontFamily: "Futura",
          },
        },
        annotations: {
          position: "back",
          yaxis: [
            {
              y: 0,
              y2: 2499,
              strokeDashArray: 1,
              borderColor: this.$color.coro["danger-200"],
              fillColor: this.$color.coro["danger-200"],
              opacity: 0.3,
              offsetX: 0,
              offsetY: -3,
              width: "100%",
              yAxisIndex: 0,
              label: {
                borderColor: this.$color.coro["danger-500"],
                borderWidth: 1,
                borderRadius: 2,
                text: "Inactive",
                textAnchor: "end",
                position: "right",
                offsetX: 0,
                offsetY: 0,
                style: {
                  background: "#fff",
                  color: "#777",
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  fontFamily: undefined,
                  cssClass: "apexcharts-yaxis-annotation-label",
                  padding: {
                    left: 5,
                    right: 5,
                    top: 0,
                    bottom: 2,
                  },
                },
              },
            },
            {
              y: 2500,
              y2: 4999,
              strokeDashArray: 1,
              borderColor: this.$color.coro["warning-200"],
              fillColor: this.$color.coro["warning-200"],
              opacity: 0.3,
              offsetX: 0,
              offsetY: -3,
              width: "100%",
              yAxisIndex: 0,
              label: {
                borderColor: this.$color.coro["warning-500"],
                borderWidth: 1,
                borderRadius: 2,
                text: "Beginner",
                textAnchor: "end",
                position: "right",
                offsetX: 0,
                offsetY: 0,
                style: {
                  background: "#fff",
                  color: "#777",
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  fontFamily: undefined,
                  cssClass: "apexcharts-yaxis-annotation-label",
                  padding: {
                    left: 5,
                    right: 5,
                    top: 0,
                    bottom: 2,
                  },
                },
              },
            },
            {
              y: 5000,
              y2: 7500,
              strokeDashArray: 1,
              borderColor: this.$color.coro["info-200"],
              fillColor: this.$color.coro["info-200"],
              opacity: 0.3,
              offsetX: 0,
              offsetY: -3,
              width: "100%",
              yAxisIndex: 0,
              label: {
                borderColor: this.$color.coro["info-500"],
                borderWidth: 1,
                borderRadius: 2,
                text: "Intermediate",
                textAnchor: "end",
                position: "right",
                offsetX: 0,
                offsetY: 0,
                style: {
                  background: "#fff",
                  color: "#777",
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  fontFamily: undefined,
                  cssClass: "apexcharts-yaxis-annotation-label",
                  padding: {
                    left: 5,
                    right: 5,
                    top: 0,
                    bottom: 2,
                  },
                },
              },
            },
            {
              y: 7501,
              y2: 8501,
              strokeDashArray: 1,
              borderColor: this.$color.coro["success-200"],
              fillColor: this.$color.coro["success-200"],
              opacity: 0.3,
              offsetX: 0,
              offsetY: -3,
              width: "100%",
              yAxisIndex: 0,
              label: {
                borderColor: this.$color.coro["success-500"],
                borderWidth: 1,
                borderRadius: 2,
                text: "Advanced",
                textAnchor: "end",
                position: "right",
                offsetX: 0,
                offsetY: 0,
                style: {
                  background: "#fff",
                  color: "#777",
                  fontSize: "0.75rem",
                  fontWeight: 400,
                  fontFamily: undefined,
                  cssClass: "apexcharts-yaxis-annotation-label",
                  padding: {
                    left: 5,
                    right: 5,
                    top: 0,
                    bottom: 2,
                  },
                },
              },
            },
          ],
        },
      },
    };
  },
  mounted: function () {
    this.getActivityWeek();
  },
  watch: {
    patientId: function () {
      this.getActivityWeek();
    },
  },
  methods: {
    getActivityWeek() {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query myActivityWeeks(
              $patientId: String!
              $from: String!
              $until: String!
            ) {
              myActivityWeeks(
                functions: [
                  { function: patientId, parameters: [$patientId] }
                  { function: excludeDeletedActivities }
                ]
                orders: [
                  { field: weekStartDate, direction: desc }
                  { field: activityDate, direction: asc }
                ]
                conditions: {
                  field: activityDate
                  operator: between
                  parameters: [$from, $until]
                }
              ) {
                edges {
                  node {
                    id
                    startDate
                    suggestedDailyStepGoalNoSport
                    daysStrengthTraining
                    planning {
                      minSessions
                      maxSessions
                      minDuration
                      maxDuration
                      feelScale
                      strengthTraining

                      frequencyLabel
                      strengthTrainingLabel
                      minIntensityLabel
                      intensityLabel(includeFlagText: false)
                      sessionDurationLabel(includeFlagText: false)
                      programDurationLabel(includeFlagText: false)
                    }
                    activityDays {
                      id
                      date
                      assignedStepLevel
                      activities {
                        id
                        date
                        time
                        intensity
                        duration
                        patientActivityType {
                          key
                          activityType {
                            name
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            from: moment()
              .clone()
              // .subtract(1, "weeks")
              .add(1, "weeks")
              .startOf("isoWeek")
              .format("YYYY-MM-DD"),
            until: moment()
              .clone()
              // .subtract(1, "weeks")
              .add(1, "weeks")
              .endOf("isoWeek")
              .format("YYYY-MM-DD"),
          },
        })
        .then((result) => {
          console.log(result);
          const { edges } = result.data.myActivityWeeks;
          const activityWeek = edges && edges.length ? edges[0].node : {};

          if (activityWeek.planning) {
            this.minSessions = activityWeek.planning.minSessions;
            this.maxSessions = activityWeek.planning.maxSessions;
            this.sessionDurationLabel =
              activityWeek.planning.sessionDurationLabel;
            this.frequencyLabel = activityWeek.planning.frequencyLabel;
            this.minIntensityLabel = activityWeek.planning.minIntensityLabel;
            this.programDurationLabel =
              activityWeek.planning.programDurationLabel;
            this.minSessionDuration = activityWeek.planning.minDuration;
            this.maxSessionDuration = activityWeek.planning.maxDuration;
            this.feelScale = activityWeek.planning.feelScale;
          }

          if (activityWeek.daysStrengthTraining) {
            this.strengthTraining = activityWeek.daysStrengthTraining;
          } else {
            this.strengthTraining = 0;
          }

          if (activityWeek.activityDays) {
            this.activityDays = activityWeek.activityDays;
          }

          let _stepGoalNoSport;

          if (activityWeek.suggestedDailyStepGoalNoSport) {
            _stepGoalNoSport = parseInt(
              activityWeek.suggestedDailyStepGoalNoSport
            );
          }
          // let _stepGoalNoSport = 1000;
          let series = [];
          let d = moment();

          // Initial.
          series.push({
            x: d.format("DD/MM"),
            y: _stepGoalNoSport,
          });

          // Predict.
          while (_stepGoalNoSport < 7500) {
            d = d.clone().add(1, "weeks");
            _stepGoalNoSport = _stepGoalNoSport * 1.1;

            series.push({
              x: d.format("DD/MM"),
              y: Math.round(_stepGoalNoSport),
            });
          }

          this.chartSeries = [
            {
              name: "Predicted steps",
              data: series,
              type: "line",
            },
          ];
          console.log(series);

          if (activityWeek.suggestedDailyStepGoalNoSport) {
            this.stepsNoSport = activityWeek.suggestedDailyStepGoalNoSport;
          }
        });
    },
  },
};
</script>

<style lang="scss">
</style>
